import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  LucideArrowDownLeft,
  LucideArrowDownRight,
  LucideArrowLeft,
  LucideArrowRight,
  LucideArrowUpLeft,
  LucideArrowUpRight,
  LucideChevronLeft,
  LucideChevronRight,
  LucideChevronsLeft,
  LucideChevronsRight,
  LucideCornerDownLeft,
  LucideCornerDownRight,
  LucideCornerUpLeft,
  LucideCornerUpRight,
  LucideDynamicIcon,
  LucideIconInput,
  LucideLogIn,
  LucideLogOut,
  LucideRedo,
  LucideUndo,
} from '@lucide/angular';

export type IconSizes = 'xs' | 's' | 'sm' | 'm' | 'l' | 'xl' | 'xxl';

/** Never list vertical icons, nor icons naming a literal side (align-left). */
const DIRECTIONAL_ICONS: readonly LucideIconInput[] = [
  LucideArrowDownLeft,
  LucideArrowDownRight,
  LucideArrowLeft,
  LucideArrowRight,
  LucideArrowUpLeft,
  LucideArrowUpRight,
  LucideChevronLeft,
  LucideChevronRight,
  LucideChevronsLeft,
  LucideChevronsRight,
  LucideCornerDownLeft,
  LucideCornerDownRight,
  LucideCornerUpLeft,
  LucideCornerUpRight,
  LucideLogIn,
  LucideLogOut,
  LucideRedo,
  LucideUndo,
];

const DIRECTIONAL_ICON_DATA = new Set<unknown>(DIRECTIONAL_ICONS.flatMap((icon) => [icon, (icon as { icon?: unknown }).icon]));
const DIRECTIONAL_ICON_NAMES = new Set<string>([
  'arrow-down-left',
  'arrow-down-right',
  'arrow-left',
  'arrow-right',
  'arrow-up-left',
  'arrow-up-right',
  'chevron-left',
  'chevron-right',
  'chevrons-left',
  'chevrons-right',
  'corner-down-left',
  'corner-down-right',
  'corner-up-left',
  'corner-up-right',
  'log-in',
  'log-out',
  'redo',
  'undo',
]);

@Component({
  selector: 'ptl-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LucideDynamicIcon],
  host: {
    '[style.color]': 'color() ?? null',
    '[class.is-directional]': 'isDirectional()',
  },
})
export class IconComponent {
  icon = input.required<LucideIconInput>();
  size = input<IconSizes>('m');
  color = input<string | undefined>(undefined);
  strokeWidth = input<number>(1.5);
  // Lucide icons are stroke-only by default; pass a colour (e.g. 'currentColor') to fill the shape.
  fill = input<string>('none');
  mirrorInRtl = input<boolean | undefined>(undefined);

  protected readonly sizeMap: Record<IconSizes, number> = {
    xs: 16,
    s: 16,
    sm: 15,
    m: 20,
    l: 24,
    xl: 24,
    xxl: 80,
  };

  protected readonly resolvedSize = computed(() => this.sizeMap[this.size()]);

  protected readonly isDirectional = computed<boolean>(() => {
    const override = this.mirrorInRtl();
    if (override !== undefined) {
      return override;
    }
    const icon = this.icon();
    return typeof icon === 'string' ? DIRECTIONAL_ICON_NAMES.has(icon) : DIRECTIONAL_ICON_DATA.has(icon);
  });
}
